import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Nav, NavItem, NavLink } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import usePermission from '../Hooks/usePermission';
import { CATEGORY_READ, ASSET_TYPE_READ, SPARE_PART_TYPE_READ, MAINTENANCE_READ } from '../constants/permissions';

import Categories from './Categories';
import AssetTypes from './AssetTypes';
import SparePartTypes from './SparePartTypes';
import ContractCategories from './ContractCategories';

const CategoriesManagement = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const visibleTabs = useMemo(() => {
    const config = [
      { key: 'work-order-categories', label: t('shared.work_order_categories', 'Work Order Categories'), icon: 'grid', path: '/settings/work-order-categories', permission: CATEGORY_READ, component: Categories },
      { key: 'asset-categories', label: t('shared.asset_categories', 'Asset Categories'), icon: 'tag', path: '/settings/asset-categories', permission: ASSET_TYPE_READ, component: AssetTypes },
      { key: 'spare-part-types', label: t('shared.spare_part_types', 'Spare Part Types'), icon: 'box', path: '/settings/spare-part-types', permission: SPARE_PART_TYPE_READ, component: SparePartTypes },
      { key: 'contract-categories', label: t('contract_categories.title', 'Contract Categories'), icon: 'file-text', path: '/settings/contract-categories', permission: MAINTENANCE_READ, component: ContractCategories },
    ];
    return config.filter(tab => hasPermission(tab.permission));
  }, [hasPermission, t]);

  const activeTab = visibleTabs.find(tab => tab.path === location.pathname);

  useEffect(() => {
    if (!activeTab && visibleTabs.length > 0) {
      navigate(visibleTabs[0].path, { replace: true });
    }
  }, [activeTab, visibleTabs, navigate]);

  useEffect(() => {
    document.title = `${t('shared.categories', 'Categories')} | Arcmedix`;
  }, [location.pathname, t]);

  if (!activeTab) return null;

  const ActiveComponent = activeTab.component;

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex align-items-center mb-4 border-bottom">
          <Nav tabs className="categories-management-tabs flex-grow-1">
            {visibleTabs.map(tab => (
              <NavItem key={tab.key}>
                <NavLink
                  className={classnames({ active: activeTab.key === tab.key })}
                  onClick={() => navigate(tab.path)}
                  style={{ cursor: 'pointer' }}
                >
                  <FeatherIcon icon={tab.icon} size={16} className="me-2" />
                  {tab.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </div>

        <ActiveComponent />
      </div>

      <style>{`
        .categories-management-tabs {
          gap: 0.5rem;
        }
        .categories-management-tabs .nav-link {
          color: #6c757d;
          background-color: #fff;
        }
        .categories-management-tabs .nav-link.active {
          color: #3980c0;
          background-color: #fff;
          border-bottom: 2px solid #3980c0;
        }
      `}</style>
    </div>
  );
};

export default CategoriesManagement;
